import moment from "moment";
import {
  allowableTime,
  canReschedule
} from "../elements/Reschedule/Reschedule";
import { orderDetailsType, settings } from "../state_management/actionTypes";
import { formatDate } from "../state_management/epics/order";
import { orderInfo, orderType } from "../state_management/reducers/order";

/**
 * Gets the number of hrs and minutes out of a `HH:mm` time cell format
 *
 * @param time Time in `HH:mm` format
 * @returns An object with hrs and mins keys which are the numbers of hours and minute respectively
 */
export const extractTimeDetails = (time: string) => {
  const hrMin = time.split(`:`);
  const hrs = parseInt(hrMin[0]) || 0;
  const mins = parseInt(hrMin[1]) || 0;

  return { hrs, mins };
};

export const getTimeStartInfo = (
  time_start: Date | string | null | undefined,
  preserve = false
): { date: string; time: string } | { date: undefined; time: undefined } => {
  try {
    const date = moment(time_start).format("YYYY-MM-DD");
    const time = moment(time_start).format("HH:mm");

    return {
      date,
      time
    };
  } catch {
    if (preserve) {
      return {
        date: undefined,
        time: undefined
      };
    } else {
      const date = moment().format("YYYY-MM-DD");
      const time = moment().format("HH:mm");
      return {
        date,
        time
      };
    }
  }
};

/**
 * Function that converts a time in minutes which is relative
 * to the start of the day to `HH:mm` format
 *
 * Example: 120 = 2 hrs, This would become 02:00 (which is 2am)
 *
 * @param minTime Time in minutes relative to start of the day
 * @returns Returns a time in `HH:mm` format
 */
export const timeFormat = (minTime: number) => {
  const stringify = (time: number | string) => {
    time = `${time}`;
    const relu = (num: number) => {
      if (num < 0) {
        return 0;
      } else {
        return num;
      }
    };
    return `${`0`.repeat(relu(2 - time.length))}${time}`;
  };
  const hr = stringify(Math.floor(minTime / 60));
  const min = stringify(Math.floor(minTime % 60));

  return `${hr}:${min}`;
};

/**
 * Convert a given hrs, min object to a number representing
 * minutes from start of day
 *
 * @param time Time object with hrs and minutes
 * @returns The amount of minutes from start of day
 */
export function getMinsTotal(time: { hrs: number; mins: number }): number;
/**
 * Convert a given time string in `HH:mm` format to a number representing
 * minutes from start of day
 *
 * @param time Time string in `HH:mm` format
 * @returns The amount of minutes from start of day
 */
export function getMinsTotal(time: string): number;
export function getMinsTotal(
  time: string | { hrs: number; mins: number }
): number {
  if (typeof time === "string") {
    let { hrs, mins } = extractTimeDetails(time);
    return hrs * 60 + mins;
  } else {
    let { hrs, mins } = time;
    return hrs * 60 + mins;
  }
}

/** We want to ensure that past times of a day are disabled */
/**
 *
 * @param day_check Selected day
 * @param time Selected timeStart expressed in minutes
 * @param windowMins window period expressed in minutes
 * @param defaultDay (Optional) Default day or today
 * @returns Whether or not time should be active
 */
export const inPast = (
  day_check: string | Date | undefined,
  time: number | undefined,
  windowMins: number,
  defaultDay?: Date
) => {
  /**
   * Essentially,
   * (day_check + time) < ((defaultDay|dateNow) + windowMins) expressed in milliseconds RETURN true
   */

  /** Minimum time in milliseconds to allowed selected time */
  const windowMilli = windowMins * 60 * 1000;

  /** Date and time as at now */
  const rawDate = defaultDay ? defaultDay : new Date();
  /** Beginning of the day */
  const dateNow = new Date(rawDate.toDateString());

  /** Current hour in the day as of now */
  const hr = rawDate.getHours();
  /** Current minute in the day as of now */
  const mins = rawDate.getMinutes();
  /** Total number of minutes currently spent in the day as of now */
  const totalNow = 60 * hr + mins;

  /** The start of the selected day */
  const orderDay = day_check
    ? new Date(new Date(day_check).toDateString())
    : dateNow;

  /** Total number of minutes of the selected time_start */
  const orderTotalTime =
    typeof time === "number" || typeof time === "string" ? time : totalNow;

  /** Number of milliseconds until the now */
  const tNow = rawDate.getTime();

  /** Number of milliseconds until the selected time period for cleaning */
  const tSel = orderDay.getTime() + orderTotalTime * 60 * 1000;

  const past = tNow + windowMilli >= tSel ? true : false;

  return past;
};

/**
 * @property {boolean} inactive sdsds
 */
type TimeCellInfo = {
  /** Boolean showing whether time cell is inactive*/
  inactive: boolean;
  /** Boolean showing whether time cell is selected*/
  isSelected: boolean;
  /** Time of time cell in `HH:mm` format*/
  Time: string;
  /** Selected Date of current session*/
  Date: Date;
  /** Fulltime which is a combination of the selected date and the time. This is in `YYYY-MM-DD HH:mm` format*/
  fullTime: string;
};

/**
 *
 * @param params Object with relevant information to determine the info of a time cell
 * @param setOrderDetails Optional function to set the state of the order Details
 *
 */
export const getTimeCellInfomation = (
  params:
    | {
        orderInfo: orderInfo;
        timeObj: {
          time: number;
          free: boolean;
        };
        orderDetails: orderType;
        serviceDuration: number;
        date: Date;
        orderWindow?: number;
        setOrderDetails?: (data: orderType) => void;
        reschedule?: never;
        settings?: never;
        selected_reschedule_time?: never;
      }
    | {
        orderInfo: orderDetailsType["data"];
        timeObj: {
          time: number;
          free: boolean;
        };
        orderDetails: orderDetailsType["data"];
        serviceDuration: number;
        date: Date;
        reschedule: true;
        settings: Partial<settings>;
        orderWindow?: never;
        setOrderDetails?: never;
        selected_reschedule_time?: string;
      }
): TimeCellInfo => {
  const {
    orderInfo,
    timeObj,
    orderDetails,
    date,
    orderWindow,
    reschedule,
    settings,
    setOrderDetails,
    selected_reschedule_time
  } = params;

  /**
   * Get the time in minutes for a particular time cell
   * Minute time here is for an order that has been created but hasn't been paid for
   * */
  const orderTime = orderInfo?.time_start
    ? getMinsTotal(getTimeStartInfo(orderInfo?.time_start).time || "")
    : undefined;

  /** This checks to see if the time, date and selected cleaner are the same with
   * that of the what was selected for an already created order that hasn't been
   * paid for if one exists
   */
  const initiallySelectedOrderTime =
    timeObj.time === orderTime &&
    getTimeStartInfo(orderInfo?.time_start).date === formatDate(date) &&
    (reschedule
      ? true // We do not care about selected cleaner during reschedules
      : orderInfo!.cleaner_id === orderDetails.cleaner_id ||
        orderDetails.random_cleaner);

  /** Returns the current time cell time in HH:mm format */
  const time = timeFormat(timeObj.time);
  /** Combines the selected day and the time of the current time cell in HH:mm format to form a date */
  const fullTime = `${formatDate(date)} ${time}`;

  /** Checks where a given time cell is in the past */
  const past = reschedule
    ? false // Rescheduling is never in the past
    : inPast(date, timeObj.time, orderWindow || 0);

  /**
   * Determines if a time cell is selected
   *
   * It checks whether the time cell + selected date in `YYYY-MM-DD HH:mm` format
   * is same with what was selected and that the time is currently not in the past
   */
  const isSelected =
    (reschedule ? selected_reschedule_time : orderDetails.time_start) ===
      fullTime && !past;

  /**
   * A time is inactive if one of these conditions are met:
   * - It is in the past
   * - It is not free and is not a previously selected time cell for an order that hasn't been paid for.
   * The is because the selected time of an unpaid order will not be free but we need to display it to the user
   * to show the user what he/she initially selected
   */
  const inactive =
    (!timeObj.free && !initiallySelectedOrderTime) ||
    (reschedule // Rescheduling have contraints which we need to obey
      ? !canReschedule(fullTime, settings) || !allowableTime(fullTime, settings)
      : false) ||
    past;

  /* Removes a previously selected start time and its associated end time if
   * the time is now inactive
   */
  if (inactive && orderDetails.time_start === fullTime && setOrderDetails) {
    setOrderDetails({
      ...orderDetails,
      time_start: null
    });
  }

  return {
    inactive,
    isSelected,
    Time: time,
    Date: date,
    fullTime
  };
};

export const prefixTransform = (
  value: string | number,
  maxCount = 2,
  prefix = "0"
): string => {
  value = value.toString();
  const num = maxCount - value.length;
  const prefixCount = Math.sign(num) === 1 ? num : 0;
  return `${prefix.repeat(prefixCount)}${value}`;
};

/**
 *
 * @returns A new enabled date for the given month (as gotten from `presentDay`) or undefined
 */
export const getNextAvailableDay = ({
  disabledDates: _disabledDates,
  presentDayDate
}: {
  disabledDates: string[]; // List of disabled dates as strings e.g. "9", "12" etc
  presentDayDate: Date; // A Date which may not necessarily be the today's Date
}) => {
  const disabledDates = _disabledDates.map((d) => parseInt(d));
  let currentDay = moment(presentDayDate).startOf("day");
  const initialMonth = currentDay.month();
  const dayOfMonth = currentDay.date();

  // Check the present day first
  if (!disabledDates.includes(dayOfMonth)) {
    return { newDate: currentDay.toDate() };
  }

  while (true) {
    currentDay.add(1, "day"); // Move to the next day
    const nextDayOfMonth = currentDay.date(); // Extract day of the month as string
    const isNewMonth = currentDay.month() !== initialMonth; // Check for new month

    if (isNewMonth) {
      return { newMonth: currentDay.toDate() };
    }

    if (!disabledDates.includes(nextDayOfMonth)) {
      return { newDate: currentDay.toDate() };
    }
  }
};
